/**
 * 挑選器內的上傳區 —— 「媒體庫裡沒有這個檔」時不必離開景點。
 *
 * ## 為什麼要有
 *
 * 原本掛一張新圖要走：離開景點 → 媒體庫 → 上傳 → 想好標籤 → 回景點 → 開挑選器
 * → 在上千筆裡找回剛剛那張。六個步驟裡有五個與「這張圖要掛哪一格」無關，而
 * 標籤還得使用者自己想——想錯了下次就找不到。
 *
 * 這裡把那六步壓成一步：拖進來就上傳，語系與資料夾標籤由呼叫端依插槽算好帶入，
 * 上傳完直接選取。使用者從頭到尾沒離開過景點。
 *
 * ## 格式在送出前就擋
 *
 * 插槽的格式要求（PNG / WAV / MP4）比媒體庫嚴格。不先擋的話，一張 JPG 會成功
 * 進媒體庫、然後在掛載時被後端退回——留下一筆沒人要的素材，而使用者只看到一句
 * 「格式不符」卻不知道檔已經傳上去了。
 *
 * 上傳一次跑一個：插槽層級一次通常只有一到五個檔，序列化換來的是進度好懂、
 * 記憶體不會被多支影片同時佔住（後端 multipart 是記憶體暫存）。
 */

import { useEffect, useRef, useState } from 'react';

import { Button } from './Button';
import { uploadMediaWithProgress, type MediaItem } from '../lib/api-client';
import {
  formatBytes,
  pickedFromDataTransfer,
  pickedFromFileList,
  validateFile,
  type PickedFile,
} from '../lib/media-upload';

type RowStatus = 'invalid' | 'queued' | 'uploading' | 'done' | 'duplicate' | 'failed';

interface Row {
  id: string;
  file: File;
  status: RowStatus;
  percent: number;
  error?: string;
}

export interface PickerUploadTarget {
  /** 上傳時帶的語系（窗屏 zh / en / jp）；插槽自己知道要放哪一語系。 */
  language?: string;
  /** 上傳時帶的資料夾標籤，例如 `3/zh`。空陣列 = 不打標籤。 */
  tags?: readonly string[];
}

interface Props extends PickerUploadTarget {
  /** 插槽要求的 MIME；不符者當場擋下，不送出。 */
  acceptMimes?: readonly string[];
  /** 每成功一個就回報（含去重命中——那也是可用的素材）。 */
  onUploaded: (item: MediaItem) => void;
}

export function MediaPickerUpload({ language, tags, acceptMimes, onUploaded }: Props): JSX.Element {
  const [rows, setRows] = useState<Row[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const uid = useRef(0);
  /** 同時只跑一個上傳；也讓 StrictMode 的重複 effect 不會送出兩次。 */
  const running = useRef(false);
  const controller = useRef<AbortController | null>(null);

  const patch = (id: string, next: Partial<Row>): void => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...next } : r)));
  };

  const enqueue = (picked: PickedFile[]): void => {
    if (picked.length === 0) return;
    setRows((prev) => [
      ...prev,
      ...picked.map((p): Row => {
        const reason = rejectReason(p.file, acceptMimes);
        return {
          id: `p${++uid.current}`,
          file: p.file,
          status: reason ? 'invalid' : 'queued',
          percent: 0,
          ...(reason ? { error: reason } : {}),
        };
      }),
    ]);
  };

  useEffect(() => {
    if (running.current) return;
    const next = rows.find((r) => r.status === 'queued');
    if (!next) return;

    running.current = true;
    const ac = new AbortController();
    controller.current = ac;
    patch(next.id, { status: 'uploading', percent: 0 });
    /** 每個 progress 事件都 setState 會讓大檔的畫面一直重繪；只在百分比變動時更新。 */
    let lastPct = -1;

    uploadMediaWithProgress(
      next.file,
      {
        ...(language ? { language } : {}),
        ...(tags && tags.length > 0 ? { tags: [...tags] } : {}),
      },
      {
        signal: ac.signal,
        onProgress: (loaded) => {
          const pct = next.file.size > 0 ? Math.floor((loaded / next.file.size) * 100) : 0;
          if (pct === lastPct) return;
          lastPct = pct;
          patch(next.id, { percent: pct });
        },
      },
    )
      .then((res) => {
        patch(next.id, { status: res.deduplicated ? 'duplicate' : 'done', percent: 100 });
        onUploaded(res.media);
      })
      .catch((e: unknown) => {
        // 挑選器關閉時中止在途上傳，此時元件已卸載，不必再寫狀態
        if (e instanceof DOMException && e.name === 'AbortError') return;
        patch(next.id, { status: 'failed', error: (e as Error).message });
      })
      .finally(() => {
        running.current = false;
        controller.current = null;
      });
  }, [rows, language, tags, onUploaded]);

  useEffect(() => {
    return () => controller.current?.abort();
  }, []);

  const pending = rows.filter((r) => r.status === 'queued' || r.status === 'uploading').length;

  return (
    <div>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept={acceptMimes?.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) enqueue(pickedFromFileList(e.target.files));
          e.target.value = '';
        }}
      />
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void pickedFromDataTransfer(e.dataTransfer).then(enqueue);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          border: `1px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
          background: dragging ? '#eff6ff' : '#fafafa',
          borderRadius: 6,
          padding: 8,
        }}
      >
        <Button variant="secondary" onClick={() => fileInput.current?.click()}>
          ⬆ 上傳新素材
        </Button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          或把檔案拖到這裡；上傳完會直接選取
          {tags && tags.length > 0 ? `，並歸入 ${tags.join('、')}` : ''}
          {pending > 0 ? `（處理中 ${pending}）` : ''}
        </span>
      </div>

      {rows.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {rows.map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{r.file.name}</span>
              <span style={{ color: '#9ca3af', flexShrink: 0 }}>{formatBytes(r.file.size)}</span>
              <span style={{ flexShrink: 0, width: 190, textAlign: 'right' }}>
                <RowStatusText row={r} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RowStatusText({ row }: { row: Row }): JSX.Element {
  switch (row.status) {
    case 'queued':
      return <span style={{ color: '#6b7280' }}>等待中</span>;
    case 'uploading':
      // 100% 只代表瀏覽器送完了，伺服器還要算 sha256、寫物件、INSERT
      return (
        <span style={{ color: '#2563eb' }}>
          {row.percent >= 100 ? '處理中' : `${row.percent}%`}
        </span>
      );
    case 'done':
      return <span style={{ color: '#047857' }}>已上傳並選取</span>;
    // 去重命中是正常結果：同一份檔案早就在庫裡，一樣可以掛
    case 'duplicate':
      return <span style={{ color: '#047857' }}>庫中已有，直接選取</span>;
    default:
      return <span style={{ color: '#b91c1c' }}>{row.error ?? '失敗'}</span>;
  }
}

/**
 * 這個檔能不能送出；可以則回 null。
 *
 * `file.type` 為空字串時一律擋下：瀏覽器認不出格式，後端就只能依 MIME 推
 * `media_type`，結果會變成 document 而掛不進插槽。與其讓它進庫再被退，不如當場講清楚。
 */
function rejectReason(file: File, acceptMimes?: readonly string[]): string | null {
  const size = validateFile(file);
  if (size) return size;
  if (!acceptMimes) return null;
  if (file.type === '') return `瀏覽器無法判斷格式（需 ${acceptMimes.join('、')}）`;
  if (!acceptMimes.includes(file.type)) return `格式為 ${file.type}，需 ${acceptMimes.join('、')}`;
  return null;
}
